import {
  Box,
  Button,
  Icon,
  Table,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import Loading from 'modules/core/components/Loading'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { CostCenterOption } from 'modules/cost_centers/components/CostCenterAssigments/components/AssignmentsManager/Picker/hooks/useCostCenterByLegalEntities'
import {
  totalDedication,
  validDedication,
} from 'modules/cost_centers/components/CostCenterAssigments/lib/dedication'
import { FormattedAssignment } from 'modules/cost_centers/components/CostCenterAssigments/lib/types'
import { useCostCenterConfiguration } from 'modules/cost_centers/hooks/useCostCenterConfiguration'
import useLegalEntities from 'modules/employees/hooks/useLegalEntities'

import StructuredValueRow from './StructuredValueRow'

interface Props {
  assignments: FormattedAssignment[]
  addNewAssignment: () => void
  onJobStructureChange: (assignmentId: string, option: CostCenterOption | null) => void
  onWorkplaceChange: (assignmentId: string, option: CostCenterOption | null) => void
  onPercentageChange: (assignmentId: string, percentage: number) => void
  onDelete: (assignmentId: string) => void
  readonly?: boolean
  employeeId?: number
}

const StructuredCostCenterPicker = ({
  assignments,
  addNewAssignment,
  onJobStructureChange,
  onWorkplaceChange,
  onPercentageChange,
  onDelete,
  readonly,
  employeeId,
}: Props) => {
  const { legalEntities } = useLegalEntities()
  const [open, toggle] = useToggle(false)

  // Get legal entity IDs to pass to each picker
  const legalEntityIds = legalEntities.map((legalEntity) => legalEntity.id)

  // Get currently selected cost center IDs (for visual indication, not exclusion)
  const selectedJobCatalogCostCenterIds = assignments
    .filter((a) => a.jobCatalogCostCenterId != null)
    .map((a) => Number(a.jobCatalogCostCenterId))

  const selectedWorkplaceCostCenterIds = assignments
    .filter((a) => a.workplaceCostCenterId != null)
    .map((a) => Number(a.workplaceCostCenterId))

  const { configurations, isLoading: isLoadingConfigurations } = useCostCenterConfiguration({
    skip: false,
  })

  const configuredStructures = configurations.map((configuration) => configuration.structure)

  useUnsafeEffect(() => {
    if (assignments.length === 0) {
      addNewAssignment()
    }
  }, [addNewAssignment, assignments])

  if (isLoadingConfigurations) {
    return (
      <Box alignItems="center" justifyContent="center" height="full" width="full" flexGrow>
        <Loading />
      </Box>
    )
  }

  const showWorkplaceColumn = configuredStructures.includes('workplace')
  const showJobStructureColumn = configuredStructures.some((structure) =>
    structure.includes('job_catalog')
  )

  // Calculate the number of CC columns for proper colSpan in total row
  const ccColumnCount = (showJobStructureColumn ? 1 : 0) + (showWorkplaceColumn ? 1 : 0)

  return (
    <Box gap="s16">
      <Table>
        <Table.Head>
          <Table.Row>
            {showJobStructureColumn && (
              <Table.HeadCell ellipsis wide>
                {i18n.t('finance.cost_centers.assignments.structured.job_structure')}
              </Table.HeadCell>
            )}
            {showWorkplaceColumn && (
              <Table.HeadCell ellipsis wide>
                {i18n.t('finance.cost_centers.assignments.structured.workplace')}
              </Table.HeadCell>
            )}
            <Table.HeadCell width={120}>%</Table.HeadCell>
            <Table.HeadCell width={40} />
          </Table.Row>
        </Table.Head>
        <Table.Body>
          <>
            {assignments.map((assignment) => (
              <StructuredValueRow
                key={assignment.id}
                showWorkplaceColumn={showWorkplaceColumn}
                showJobStructureColumn={showJobStructureColumn}
                assignment={assignment}
                selectedJobCatalogCostCenterIds={selectedJobCatalogCostCenterIds}
                selectedWorkplaceCostCenterIds={selectedWorkplaceCostCenterIds}
                legalEntityIds={legalEntityIds}
                readonly={readonly}
                onDelete={onDelete}
                onJobStructureChange={onJobStructureChange}
                onWorkplaceChange={onWorkplaceChange}
                onPercentageChange={onPercentageChange}
                employeeId={employeeId}
              />
            ))}
            <Table.Row background="grey200">
              <Table.Cell colSpan={ccColumnCount}>
                <Box flexDirection="row" gap="s8" alignItems="center">
                  <Text.Normal size="200" weight="semibold" color="grey1000">
                    {i18n.t('finance.employee_dimensions_info.modals.set_assignments.total')}
                  </Text.Normal>
                  <Box alignSelf="center">
                    <Tooltip.Alt
                      size="small"
                      attachment="bottom center"
                      open={open}
                      toggle={toggle}
                      label={i18n.t(
                        'finance.cost_centers.assignments.structured.add_distribution_info'
                      )}
                    >
                      <Icon.Medium icon="info-circle-line" color="grey600" />
                    </Tooltip.Alt>
                  </Box>
                </Box>
              </Table.Cell>
              <Table.Cell width={120}>
                <Text.Normal
                  size="200"
                  weight="semibold"
                  color={validDedication(assignments) ? 'grey1000' : 'radicalRed1200'}
                >
                  {totalDedication(assignments)}
                </Text.Normal>
              </Table.Cell>
              <Table.Cell />
            </Table.Row>
          </>
        </Table.Body>
      </Table>

      {/* we don't want to support custom distributions yet
      {!readonly && (
        <Button onClick={addNewAssignment} type="secondary" iconName="add">
          {i18n.t('finance.cost_centers.assignments.structured.add_distribution')}
        </Button>
      )} */}
    </Box>
  )
}

export default StructuredCostCenterPicker
